import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../hooks/useAuth'
import { useContentLibrary } from '../hooks/useContentLibrary'
import { useUsageControl } from '../hooks/useUsageControl'
import { UsageWarningBanner } from '../components/UsageWarningBanner'
import { supabase } from '../lib/supabase'
import {
  Wand2,
  Sparkles,
  Download,
  Copy,
  Trash2,
  X,
  Loader2,
  Square,
  Image as ImageIcon,
  BookOpen,
  Monitor,
  AlertCircle,
} from 'lucide-react'

type AspectRatio = '1:1' | '2:3' | '16:9'
type Style = 'default' | 'cinematic' | '3d-render' | 'line-art' | 'anime'

interface GeneratedImage {
  id: string
  file_url: string
  name: string
  metadata: {
    prompt?: string
    aspect_ratio?: string
    style?: string
  }
  created_at: string
}

export function VisualStudioTool() {
  const { user } = useAuth()
  const { content, loadContent, deleteContent } = useContentLibrary()
  const { stats } = useUsageControl()
  const [prompt, setPrompt] = useState('')
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('1:1')
  const [style, setStyle] = useState<Style>('default')
  const [generating, setGenerating] = useState(false)
  const [enhancing, setEnhancing] = useState(false)
  const [selectedImage, setSelectedImage] = useState<GeneratedImage | null>(null)
  const [error, setError] = useState<string | null>(null)

  const IMAGE_CREDIT_COST = 5 // Image generation costs 5 credits

  // Filter images for image-generator tool (Visual Studio)
  const images = content.filter((item) =>
    (item.tool_id === 'visual-studio' || item.tool_id === 'image-generator') &&
    item.type === 'image'
  )

  useEffect(() => {
    if (user) {
      loadContent()
    }
  }, [user])

  const enhancePrompt = async () => {
    if (!prompt.trim()) return

    setEnhancing(true)
    setError(null)

    try {
      // Call Groq API to enhance the prompt
      const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + (process.env.REACT_APP_GROQ_API_KEY || ''),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'mixtral-8x7b-32768',
          messages: [
            {
              role: 'system',
              content:
                'You are an expert at writing detailed, creative image generation prompts. Enhance the user prompt to be more descriptive, vivid, and specific. Include details about lighting, composition, style, and quality. Keep it concise (under 200 words).',
            },
            {
              role: 'user',
              content: 'Enhance this image prompt: ' + prompt,
            },
          ],
          temperature: 0.7,
          max_tokens: 300,
        }),
      })

      if (!response.ok) throw new Error('Failed to enhance prompt')

      const data = await response.json()
      const enhancedPrompt = data.choices[0]?.message?.content || prompt

      setPrompt(enhancedPrompt)
    } catch (err: any) {
      console.error('Prompt enhancement error:', err)
      setError('Failed to enhance prompt. Using original.')
    } finally {
      setEnhancing(false)
    }
  }

  const generateImage = async () => {
    if (!prompt.trim() || !user) return

    setGenerating(true)
    setError(null)

    try {
      // Get the current session to ensure auth token is fresh
      const { data: { session } } = await supabase.auth.getSession()

      if (!session) {
        throw new Error('Please log in to generate images')
      }

      console.log('Session exists:', !!session)
      console.log('Access token exists:', !!session.access_token)

      const { data, error: functionError } = await supabase.functions.invoke('generate-image', {
        body: {
          prompt: prompt,
          aspect_ratio: aspectRatio,
          style: style,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      console.log('Function response:', { data, error: functionError })

      if (functionError) throw functionError
      if (data.error) throw new Error(data.message || data.error)

      // Reload content to show new image
      await loadContent()
    } catch (err: any) {
      console.error('Image generation error:', err)
      setError(err.message || 'Failed to generate image')
    } finally {
      setGenerating(false)
    }
  }

  const downloadImage = async (image: GeneratedImage) => {
    const imageUrl = image.file_url || (image as any).content
    if (!imageUrl) return

    try {
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'visual-studio-' + Date.now() + '.png'
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Download error:', err)
    }
  }

  const copyPrompt = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const handleDeleteImage = async (id: string) => {
    if (confirm('Delete this image?')) {
      await deleteContent(id)
      setSelectedImage(null)
    }
  }

  const aspectRatios = [
    { value: '1:1', label: 'Square', icon: Square, desc: 'Social Media' },
    { value: '2:3', label: 'Portrait', icon: BookOpen, desc: 'Book Covers' },
    { value: '16:9', label: 'Landscape', icon: Monitor, desc: 'Blog/Website' },
  ]

  const styles = [
    { value: 'default', label: 'Default', desc: 'Natural style' },
    { value: 'cinematic', label: 'Cinematic', desc: 'Film-like' },
    { value: '3d-render', label: '3D Render', desc: 'Volumetric' },
    { value: 'line-art', label: 'Line Art', desc: 'Minimalist' },
    { value: 'anime', label: 'Anime', desc: 'Illustration' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Visual Studio
          </h1>
          <p className="text-slate-400">Create stunning images and book covers with AI</p>
        </div>

        {/* Usage Warning Banner */}
        <div className="mb-6">
          <UsageWarningBanner />
        </div>

        {/* Main Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Settings */}
          <div className="lg:col-span-1 space-y-6">
            {/* Aspect Ratio */}
            <div className="bg-slate-800/50 backdrop-blur rounded-xl p-4 border border-slate-700/50">
              <h3 className="text-sm font-semibold mb-3 text-slate-300">Aspect Ratio</h3>
              <div className="space-y-2">
                {aspectRatios.map((ratio) => {
                  const Icon = ratio.icon
                  return (
                    <button
                      key={ratio.value}
                      onClick={() => setAspectRatio(ratio.value as AspectRatio)}
                      className={
                        'w-full p-3 rounded-lg border-2 transition-all text-left ' +
                        (aspectRatio === ratio.value
                          ? 'border-purple-500 bg-purple-500/20'
                          : 'border-slate-700 hover:border-slate-600 bg-slate-800/30')
                      }
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5" />
                        <div>
                          <div className="font-medium text-sm">{ratio.label}</div>
                          <div className="text-xs text-slate-400">{ratio.desc}</div>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Style Presets */}
            <div className="bg-slate-800/50 backdrop-blur rounded-xl p-4 border border-slate-700/50">
              <h3 className="text-sm font-semibold mb-3 text-slate-300">Style</h3>
              <div className="space-y-2">
                {styles.map((styleOption) => (
                  <button
                    key={styleOption.value}
                    onClick={() => setStyle(styleOption.value as Style)}
                    className={
                      'w-full p-3 rounded-lg border-2 transition-all text-left ' +
                      (style === styleOption.value
                        ? 'border-pink-500 bg-pink-500/20'
                        : 'border-slate-700 hover:border-slate-600 bg-slate-800/30')
                    }
                  >
                    <div className="font-medium text-sm">{styleOption.label}</div>
                    <div className="text-xs text-slate-400">{styleOption.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Prompt Bar */}
            <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6 border border-slate-700/50">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="A futuristic city with neon lights, cyberpunk style, cinematic lighting, 4k..."
                className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 min-h-[100px] resize-none"
              />

              <div className="flex gap-3 mt-4">
                <button
                  onClick={enhancePrompt}
                  disabled={!prompt.trim() || enhancing}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Wand2 className={'h-4 w-4 ' + (enhancing ? 'animate-spin' : '')} />
                  {enhancing ? 'Enhancing...' : 'Enhance'}
                </button>

                <button
                  onClick={generateImage}
                  disabled={!prompt.trim() || generating || stats?.isExceeded}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  title={stats?.isExceeded ? 'Credit limit exceeded - upgrade to continue' : ''}
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Dreaming up your image...
                    </>
                  ) : stats?.isExceeded ? (
                    <>
                      <AlertCircle className="h-5 w-5" />
                      Credit Limit Reached
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-5 w-5" />
                      Generate ({IMAGE_CREDIT_COST} credits)
                    </>
                  )}
                </button>
              </div>

              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mt-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm"
                  >
                    {error}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Gallery */}
            <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6 border border-slate-700/50">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Your Gallery ({images.length})
              </h2>

              {images.length === 0 ? (
                <div className="text-center py-12">
                  <Sparkles className="h-16 w-16 mx-auto mb-4 text-slate-600" />
                  <p className="text-slate-400">No images yet. Start creating!</p>
                </div>
              ) : (
                <motion.div
                  layout
                  className="grid grid-cols-2 sm:grid-cols-3 gap-4"
                >
                  <AnimatePresence>
                    {images.map((image, index) => (
                      <motion.button
                        key={image.id}
                        layout
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => setSelectedImage(image as GeneratedImage)}
                        className="aspect-square rounded-lg overflow-hidden border-2 border-slate-700 hover:border-purple-500 transition-all group relative"
                      >
                        <img
                          src={image.file_url || (image as any).content || ''}
                          alt={image.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="absolute bottom-2 left-2 right-2">
                            <p className="text-xs text-white truncate">{image.name}</p>
                          </div>
                        </div>
                      </motion.button>
                    ))}
                  </AnimatePresence>
                </motion.div>
              )}
            </div>
          </div>
        </div>

        {/* Lightbox */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedImage(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                onClick={(e) => e.stopPropagation()}
                className="max-w-4xl w-full bg-slate-900 rounded-xl overflow-hidden border border-slate-700"
              >
              <div className="p-4 flex items-center justify-between border-b border-slate-700">
                <h3 className="font-semibold truncate flex-1">{selectedImage.name}</h3>
                <button
                  onClick={() => setSelectedImage(null)}
                  className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-4">
                <img
                  src={selectedImage.file_url || (selectedImage as any).content}
                  alt={selectedImage.name}
                  className="w-full rounded-lg"
                />
              </div>

              {selectedImage.metadata?.prompt && (
                <div className="p-4 border-t border-slate-700">
                  <p className="text-sm text-slate-400 mb-1">Prompt:</p>
                  <p className="text-sm">{selectedImage.metadata.prompt}</p>
                </div>
              )}

              <div className="p-4 flex gap-2 border-t border-slate-700">
                <button
                  onClick={() => downloadImage(selectedImage)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Download
                </button>
                {selectedImage.metadata?.prompt && (
                  <button
                    onClick={() => copyPrompt(selectedImage.metadata.prompt || '')}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                  >
                    <Copy className="h-4 w-4" />
                    Copy Prompt
                  </button>
                )}
                <button
                  onClick={() => handleDeleteImage(selectedImage.id)}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 rounded-lg transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
